// Story 1.32 — TanStack Query mutation:
// POST /api/v1/admin/financial-tools/ledger-adjustment.
//
// Credit or debit a user's wallet via BalanceProvenance::ManualAdjustment.
// No provider call; no related entity. Insufficient balance on debit
// returns 422 INSUFFICIENT_BALANCE.

import { useMutation } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type LedgerAdjustmentDirection = "credit" | "debit";

export type LedgerAdjustmentInput = {
  user_id: string;
  direction: LedgerAdjustmentDirection;
  amount_cents: number;
  justification: string;
  idempotency_key: string;
};

export type LedgerAdjustmentResponse = {
  data: {
    ledger_entry_id: string;
    direction: LedgerAdjustmentDirection;
    amount_cents: number;
    new_balance_cents: number;
  };
};

export class LedgerAdjustmentError extends Error {
  code?: string;
  field?: string;
  required_cents?: number;
  available_cents?: number;

  constructor(message: string) {
    super(message);
    this.name = "LedgerAdjustmentError";
  }
}

async function ledgerAdjustment(
  input: LedgerAdjustmentInput,
): Promise<LedgerAdjustmentResponse> {
  const { idempotency_key, ...body } = input;
  const response = await fetch(
    "/api/v1/admin/financial-tools/ledger-adjustment",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": idempotency_key,
      },
      body: JSON.stringify(body),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Ledger adjustment failed (HTTP ${response.status})`;
    let parsed: Record<string, unknown> | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      parsed = raw?.error;
    } catch {
      // body not JSON
    }
    const err = new LedgerAdjustmentError(message);
    err.code = parsed?.code as string | undefined;
    err.field = parsed?.field as string | undefined;
    err.required_cents = parsed?.required_cents as number | undefined;
    err.available_cents = parsed?.available_cents as number | undefined;
    throw err;
  }
  return (await response.json()) as LedgerAdjustmentResponse;
}

export function useExecuteLedgerAdjustment() {
  return useMutation({
    mutationFn: ledgerAdjustment,
  });
}
